import { useEffect, useState } from "react";
import type { ScheduleMonthDto } from "@madamuh/domain";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  applyAdminOccurrenceOverride,
  deleteAdminOccurrence,
  fetchAdminScheduleMonth,
  generateAdminScheduleMonth,
  publishAdminScheduleMonth,
  validateAdminScheduleMonth
} from "../../shared/api/admin-schedule";
import { formatTenantDateTime, formatTenantIsoDateTime } from "../../shared/lib/tenantDateTime";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";

const adminLinks = [
  { to: "/t/:tenantSlug/teacher/admin", label: "管理ホーム" },
  { to: "/t/:tenantSlug/teacher/admin/events", label: "イベント管理" }
];

function formatDateTime(value: string | null): string {
  if (!value) {
    return "未実行";
  }

  return formatTenantDateTime(value);
}

type LocationState = {
  feedbackMessage?: string;
} | null;

function buildDeleteConfirmationMessage(occurrence: ScheduleMonthDto["occurrences"][number]): string {
  const lines = [
    "予定を削除しますか？",
    `${occurrence.lessonTitle} ${formatTenantIsoDateTime(occurrence.startAt)} - ${formatTenantIsoDateTime(occurrence.endAt)} をこのスケジュールから削除します。`
  ];

  if (occurrence.sourceType === "manual") {
    lines.push("個別追加した予定は Generate では再作成されません。");
  } else {
    lines.push("再度 Generate すると、定期枠が有効な場合は再作成されます。");
    lines.push("一時的に削除しただけの場合は、再度 Generate で復旧できます。");
    lines.push("今後も不要な場合は、レッスン詳細の定期枠を無効化してください。");
  }

  return lines.join("\n");
}

export function AdminScheduleMonthPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant", yyyymm = "" } = useParams();
  const locationState = location.state as LocationState;
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [scheduleMonth, setScheduleMonth] = useState<ScheduleMonthDto | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionState, setActionState] = useState<"idle" | "running">("idle");
  const [occurrenceActionKey, setOccurrenceActionKey] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(locationState?.feedbackMessage ?? null);
  const [showExistingWarnings, setShowExistingWarnings] = useState(false);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));

  useEffect(() => {
    setShowExistingWarnings(false);
  }, [scheduleMonth?.yearMonth, scheduleMonth?.draftRevision, scheduleMonth?.publishedRevision]);

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole || !yyyymm) {
      return;
    }

    let cancelled = false;

    async function loadScheduleMonth() {
      setLoadState("loading");
      setLoadError(null);

      try {
        const nextScheduleMonth = await fetchAdminScheduleMonth(tenantSlug, yyyymm);

        if (cancelled) {
          return;
        }

        setScheduleMonth(nextScheduleMonth);
        setLoadState("idle");
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (isUnauthenticatedApiError(error)) {
          navigate(`/t/${tenantSlug}/entry`, { replace: true });
          return;
        }

        setLoadState("error");
        setLoadError(getProtectedApiErrorMessage(error, "schedule month の取得に失敗しました"));
      }
    }

    void loadScheduleMonth();

    return () => {
      cancelled = true;
    };
  }, [hasAdminRole, isSessionReady, navigate, tenantSlug, yyyymm]);

  async function handleAction(action: "generate" | "validate" | "publish") {
    if (!yyyymm) {
      return;
    }

    setActionState("running");
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      const nextScheduleMonth =
        action === "generate"
          ? await generateAdminScheduleMonth(tenantSlug, yyyymm)
          : action === "validate"
            ? await validateAdminScheduleMonth(tenantSlug, yyyymm)
            : await publishAdminScheduleMonth(tenantSlug, yyyymm, {
                expectedDraftRevision: scheduleMonth?.draftRevision ?? 0
              });

      setScheduleMonth(nextScheduleMonth);
      setFeedbackMessage(
        action === "generate"
          ? "schedule month を generate しました。"
          : action === "validate"
            ? "schedule month を validate しました。"
            : "schedule month を publish しました。"
      );
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "schedule month 操作に失敗しました"));
    } finally {
      setActionState("idle");
    }
  }

  async function handleCancelOccurrence(occurrenceId: string, expectedRevision: number) {
    if (!scheduleMonth) {
      return;
    }

    setOccurrenceActionKey(`cancel:${occurrenceId}`);
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      await applyAdminOccurrenceOverride(tenantSlug, occurrenceId, {
        expectedRevision,
        overrideType: "cancel",
        causeType: "clubDecision",
        newDate: null,
        newStartAt: null,
        newEndAt: null,
        publicNote: null,
        operatorNote: "schedule month quick cancel",
        adminNote: "schedule month quick cancel"
      });

      const nextScheduleMonth = await fetchAdminScheduleMonth(tenantSlug, scheduleMonth.yearMonth);
      setScheduleMonth(nextScheduleMonth);
      setFeedbackMessage("この回を休講にしました。");
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "occurrence の休講処理に失敗しました"));
    } finally {
      setOccurrenceActionKey(null);
    }
  }

  async function handleDeleteOccurrence(occurrence: ScheduleMonthDto["occurrences"][number]) {
    if (!window.confirm(buildDeleteConfirmationMessage(occurrence))) {
      return;
    }

    setOccurrenceActionKey(`delete:${occurrence.occurrenceId}`);
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      const nextScheduleMonth = await deleteAdminOccurrence(tenantSlug, occurrence.occurrenceId, {
        expectedRevision: occurrence.revision
      });
      setScheduleMonth(nextScheduleMonth);
      setFeedbackMessage("予定を削除しました。");
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "予定の削除に失敗しました"));
    } finally {
      setOccurrenceActionKey(null);
    }
  }

  const validationIssues = scheduleMonth?.validationIssues ?? [];
  const blockingIssues = validationIssues.filter((item) => item.severity === "blocking");
  const warningIssues = validationIssues.filter((item) => item.severity === "warning");
  const currentChangeWarnings = warningIssues.filter((item) => item.changeScope === "currentChange");
  const existingWarnings = warningIssues.filter((item) => item.changeScope !== "currentChange");

  return (
    <OperatorPageScaffold
      currentArea="admin"
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      title="Admin Schedule Month"
      description="月次 schedule の generate / validate / publish を行う最小画面です。"
      links={adminLinks}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {routeStatus === "redirecting" ? <p>Entry へ移動しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p>この画面は admin role のみ利用できます。</p> : null}
        {loadState === "loading" ? <p>schedule month を読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}
        {feedbackMessage ? <p>{feedbackMessage}</p> : null}

        {scheduleMonth ? (
          <>
            <section style={{ display: "grid", gap: 8 }}>
              <strong style={{ fontSize: 20 }}>{scheduleMonth.yearMonth}</strong>
              <div style={{ fontSize: 14, opacity: 0.8 }}>status: {scheduleMonth.status}</div>
              <div style={{ fontSize: 14, opacity: 0.8 }}>draftRevision: {scheduleMonth.draftRevision}</div>
              <div style={{ fontSize: 14, opacity: 0.8 }}>publishedRevision: {scheduleMonth.publishedRevision}</div>
              <div style={{ fontSize: 14, opacity: 0.8 }}>hasUnpublishedChanges: {String(scheduleMonth.hasUnpublishedChanges)}</div>
              <div style={{ fontSize: 14, opacity: 0.8 }}>lastGeneratedAt: {formatDateTime(scheduleMonth.lastGeneratedAt)}</div>
              <div style={{ fontSize: 14, opacity: 0.8 }}>lastValidatedAt: {formatDateTime(scheduleMonth.lastValidatedAt)}</div>
              <div style={{ fontSize: 14, opacity: 0.8 }}>lastPublishedAt: {formatDateTime(scheduleMonth.lastPublishedAt)}</div>
            </section>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button type="button" onClick={() => void handleAction("generate")} disabled={actionState === "running"}>
                {actionState === "running" ? "処理中..." : "Generate"}
              </button>
              <button type="button" onClick={() => void handleAction("validate")} disabled={actionState === "running"}>
                {actionState === "running" ? "処理中..." : "Validate"}
              </button>
              <button type="button" onClick={() => void handleAction("publish")} disabled={actionState === "running"}>
                {actionState === "running" ? "処理中..." : "Publish"}
              </button>
            </div>

            <section style={{ display: "grid", gap: 6 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>個別予定を追加</h2>
              <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>
                この画面からの直接追加は次の段階で対応します。今はレッスン詳細から個別予定を追加してください。
              </p>
              <Link to={`/t/${tenantSlug}/teacher/admin/lessons`} style={{ fontSize: 14 }}>
                レッスン詳細から個別予定を追加してください。
              </Link>
            </section>

            <section style={{ display: "grid", gap: 8 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Validation</h2>
              <div style={{ fontSize: 14 }}>blocking: {scheduleMonth.blockingErrorCount}</div>
              <div style={{ fontSize: 14 }}>warning: {scheduleMonth.warningCount}</div>
              {validationIssues.length === 0 ? <p style={{ margin: 0 }}>issue はありません。</p> : null}

              {blockingIssues.length > 0 ? (
                <section style={{ display: "grid", gap: 8 }}>
                  <strong style={{ fontSize: 15, color: "#b42318" }}>blocking error</strong>
                  {blockingIssues.map((validationIssue) => (
                    <article
                      key={`${validationIssue.issueCode}:${validationIssue.occurrenceId ?? "month"}`}
                      style={{
                        border: "1px solid #fda29b",
                        background: "#fef3f2",
                        borderRadius: 12,
                        padding: 12,
                        display: "grid",
                        gap: 6
                      }}
                    >
                      <strong>{validationIssue.severity}</strong>
                      <div>{validationIssue.message}</div>
                      <div style={{ fontSize: 12, opacity: 0.8 }}>
                        occurrence: {validationIssue.occurrenceId ?? "month-level"}
                      </div>
                    </article>
                  ))}
                </section>
              ) : null}

              {warningIssues.length > 0 ? (
                <section style={{ display: "grid", gap: 12 }}>
                  <section style={{ display: "grid", gap: 8 }}>
                    <strong style={{ fontSize: 15 }}>今回の変更に関する警告 ({currentChangeWarnings.length})</strong>
                    {currentChangeWarnings.length === 0 ? (
                      <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>
                        今回の generate / validate で新たに追加・更新された occurrence に起因する警告はありません。
                      </p>
                    ) : (
                      currentChangeWarnings.map((validationIssue) => (
                        <article
                          key={`${validationIssue.issueCode}:${validationIssue.occurrenceId ?? "month"}:current`}
                          style={{
                            border: "1px solid #fecdca",
                            background: "#fff6ed",
                            borderRadius: 12,
                            padding: 12,
                            display: "grid",
                            gap: 6
                          }}
                        >
                          <strong>{validationIssue.severity}</strong>
                          <div>{validationIssue.message}</div>
                          <div style={{ fontSize: 12, opacity: 0.8 }}>
                            occurrence: {validationIssue.occurrenceId ?? "month-level"}
                          </div>
                        </article>
                      ))
                    )}
                  </section>

                  <section style={{ display: "grid", gap: 8 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <strong style={{ fontSize: 15 }}>既存の警告 ({existingWarnings.length})</strong>
                      {existingWarnings.length > 0 ? (
                        <button type="button" onClick={() => setShowExistingWarnings((current) => !current)}>
                          {showExistingWarnings ? "閉じる" : "表示する"}
                        </button>
                      ) : null}
                    </div>
                    <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>
                      既存 publish 済み occurrence や今回未変更の occurrence に関する警告です。
                    </p>
                    {existingWarnings.length === 0 ? (
                      <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>既存の警告はありません。</p>
                    ) : null}
                    {showExistingWarnings
                      ? existingWarnings.map((validationIssue) => (
                        <article
                          key={`${validationIssue.issueCode}:${validationIssue.occurrenceId ?? "month"}:existing`}
                          style={{
                            border: "1px solid #e5e7eb",
                            background: "#f8fafc",
                            borderRadius: 12,
                            padding: 12,
                            display: "grid",
                            gap: 6
                          }}
                        >
                          <strong>{validationIssue.severity}</strong>
                          <div>{validationIssue.message}</div>
                          <div style={{ fontSize: 12, opacity: 0.8 }}>
                            occurrence: {validationIssue.occurrenceId ?? "month-level"}
                          </div>
                        </article>
                      ))
                      : null}
                  </section>
                </section>
              ) : null}
            </section>

            <section style={{ display: "grid", gap: 8 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Occurrences</h2>
              {scheduleMonth.occurrences.map((occurrence) => (
                <article
                  key={occurrence.occurrenceId}
                  style={{
                    border: "1px solid #e5e5e5",
                    borderRadius: 12,
                    padding: 12,
                    display: "grid",
                    gap: 6
                  }}
                >
                  <strong>{occurrence.lessonTitle}</strong>
                  <div style={{ fontSize: 14, opacity: 0.8 }}>
                    revision {occurrence.revision} / {occurrence.syncState} / {occurrence.resolutionType}
                  </div>
                  <div style={{ fontSize: 14, opacity: 0.8 }}>
                    {formatTenantIsoDateTime(occurrence.startAt)} - {formatTenantIsoDateTime(occurrence.endAt)}
                  </div>
                  {occurrence.publicNote ? <div style={{ fontSize: 14 }}>{occurrence.publicNote}</div> : null}
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Link to={`/t/${tenantSlug}/teacher/admin/occurrences/${occurrence.occurrenceId}`}>
                      詳細/編集
                    </Link>
                    {occurrence.sourceType === "generator" && occurrence.lifecycleStatus !== "cancelled" ? (
                      <button
                        type="button"
                        onClick={() => void handleCancelOccurrence(occurrence.occurrenceId, occurrence.revision)}
                        disabled={occurrenceActionKey === `cancel:${occurrence.occurrenceId}`}
                      >
                        {occurrenceActionKey === `cancel:${occurrence.occurrenceId}` ? "処理中..." : "休講"}
                      </button>
                    ) : null}
                    {occurrence.sourceType === "generator" ? (
                      <Link to={`/t/${tenantSlug}/teacher/admin/occurrences/${occurrence.occurrenceId}?action=reschedule`}>
                        振替
                      </Link>
                    ) : null}
                    {!occurrence.publishedEver ? (
                      <button
                        type="button"
                        onClick={() => void handleDeleteOccurrence(occurrence)}
                        disabled={occurrenceActionKey === `delete:${occurrence.occurrenceId}`}
                      >
                        {occurrenceActionKey === `delete:${occurrence.occurrenceId}` ? "処理中..." : "削除"}
                      </button>
                    ) : null}
                  </div>
                  {occurrence.publishedEver ? (
                    <div style={{ fontSize: 12, opacity: 0.8 }}>
                      公開済みの予定は削除できません。休講または変更として処理してください。
                    </div>
                  ) : occurrence.sourceType === "generator" ? (
                    <div style={{ fontSize: 12, opacity: 0.8 }}>
                      削除後も、定期枠が有効なら再度 Generate で再作成されます。
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, opacity: 0.8 }}>
                      個別追加した予定は、削除すると Generate では戻りません。
                    </div>
                  )}
                </article>
              ))}
            </section>
          </>
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}
